/**
 * Pass canvas node props through to MUI / host components with minimal filtering.
 *
 * Agents may set any prop the underlying @material-ui/core v4 component supports
 * (variant, style, className, classes, InputProps, color, size, etc.).
 *
 * Only strip canvas-runtime keys that are not valid component props.
 */

/** Keys owned by the canvas runtime — never forwarded to MUI/DOM. */
export const CANVAS_INTERNAL_KEYS = new Set([
  'statePath',
  // event wiring lives on node.events, not props
  'events',
  'bindings',
  'children',
  'component',
  'version',
  'id',
  'fallback',
  'layout',
]);

/**
 * @param {object} props - node.props from the canvas document
 * @param {object} [options]
 * @param {string[]} [options.omit] - extra keys to strip (component-specific)
 * @param {object} [options.defaults] - applied first (overridable by props)
 * @param {object} [options.overrides] - applied last (always win — e.g. controlled value/onChange)
 * @param {Record<string, string>} [options.rename] - map canvas alias → MUI prop (e.g. { text: null } handled by caller)
 */
export function passthroughMuiProps(props = {}, options = {}) {
  const omit = new Set([
    ...CANVAS_INTERNAL_KEYS,
    ...(options.omit || []),
  ]);
  const out = { ...(options.defaults || {}) };
  Object.keys(props || {}).forEach((key) => {
    if (omit.has(key)) return;
    if (props[key] === undefined) return;
    out[key] = props[key];
  });
  return { ...out, ...(options.overrides || {}) };
}

/** Merge style objects without dropping agent-provided styles. */
export function mergeStyles(...styles) {
  return Object.assign({}, ...styles.filter(Boolean));
}
